/**
 * Where a dropped file goes (2026-11-03, operator: "maybe a prompt indicated where to drop it"). While a file is
 * dragged over OPButtons anywhere no drop zone takes it (`navigate/browserGuards.ts`), this panel says where audio
 * goes and is itself a drop zone: dropped on it, the files go into the Audio library through the same queue as the
 * Audio section (`enqueueAudio`, whose window shows each import). A drag that is not audio is told what OPButtons
 * takes, and the panel takes nothing.
 *
 * Body-mounted and fixed to the WINDOW, not the iPad frame: it follows a drag from outside the app, which knows
 * nothing of the shell's scale. It catches no pointer except during a file drag (it only exists then). At the bottom
 * of the window, so it covers as little as it can of a section's own drop zones. Not audio: no drop zone, the
 * no-drop cursor stays.
 */
import { Portal } from 'solid-js/web';
import { Show, type JSX } from 'solid-js';
import { dropCatch } from '../navigate/browserGuards.ts';
import { enqueueAudio, looksLikeAudio } from '../timeline/testTrackStore.ts';
import { pushToast } from '../ui/toast';

export function DropCatch(): JSX.Element {
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    const all = [...(e.dataTransfer?.files ?? [])];
    const audio = all.filter(looksLikeAudio);
    if (!audio.length) { pushToast('warn', 'OPButtons takes audio files: mp3, wav, flac, m4a, aac, aif, ogg or opus.'); return; }
    if (audio.length < all.length) pushToast('warn', `Added the audio; left out ${all.length - audio.length} file${all.length - audio.length === 1 ? '' : 's'} that ${all.length - audio.length === 1 ? 'is' : 'are'} not audio.`);
    void enqueueAudio(audio);
  };
  return (
    <Show when={dropCatch()}>
      {(c) => (
        <Portal>
          <div class="nv-dropcatch" data-testid="drop-catch" role="status" aria-live="polite">
            <Show
              when={c().audio !== false}
              fallback={
                <div class="nv-dropcatch-panel" data-kind="other">
                  <b>OPButtons takes audio files</b>
                  <p class="nv-drop-hint">mp3, wav, flac, m4a, aac, aif, ogg or opus. Anything else cannot be dropped here.</p>
                </div>
              }
            >
              <div class="nv-dropcatch-panel" data-drop-catch onDrop={onDrop}>
                <div class="nav-well nv-drop" data-over={c().over ? 'true' : undefined}>
                  <b>Drop here to add it to your Audio library</b>
                  <p class="nv-drop-hint">Or on an audio pad, a timeline's song row, or in Navigate &gt; Audio.</p>
                </div>
              </div>
            </Show>
          </div>
        </Portal>
      )}
    </Show>
  );
}
